import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface AgentCard {
  id: string;
  name: string;
  module: string;
  icon: string;
  pitch: string;
  model: string;
  phase: 1 | 2 | 3 | 4;
}

const AGENTS: AgentCard[] = [
  // Phase 1
  { id: 'onboardeur', name: 'Onboardeur', module: 'Extension', icon: '🚀', pitch: "Crée un dossier client à partir d'un matricule fiscal — RNE, classification SCE, régime fiscal.", model: 'Haiku 4.5', phase: 1 },
  { id: 'echeancier', name: 'Échéancier', module: 'Extension', icon: '📅', pitch: 'Calendrier fiscal multi-clients — TVA, IS, RAS, CNSS, TFP, Foprolos triés par urgence.', model: 'Haiku 4.5', phase: 1 },
  { id: 'comptable', name: 'Comptable', module: 'Comptabilité', icon: '📒', pitch: "Saisie d'écriture SCE NC 01 — classification compte, calcul TVA, équilibre vérifié.", model: 'Sonnet 4.6', phase: 1 },
  { id: 'facturier', name: 'Facturier', module: 'Facturation', icon: '⚡', pitch: 'Émission de facture conforme TEIF + soumission TTN El Fatoora.', model: 'Sonnet 4.6', phase: 1 },
  { id: 'recouvreur', name: 'Recouvreur', module: 'Facturation', icon: '✉️', pitch: 'Campagne de relance 4 niveaux — courtoise → mise en demeure COC.', model: 'Sonnet 4.6', phase: 1 },
  // Phase 2
  { id: 'rapprocheur', name: 'Rapprocheur', module: 'Comptabilité', icon: '🔗', pitch: 'Rapprochement bancaire — parsers OFX/CSV (BIAT, Attijari, BH) + matching 4 passes.', model: 'Sonnet 4.6', phase: 2 },
  { id: 'fiscaliste', name: 'Fiscaliste', module: 'Fiscal', icon: '🧾', pitch: 'Déclarations TVA / IS / RAS — calcul automatique, échéances, citations Code IRPP/IS.', model: 'Sonnet 4.6', phase: 2 },
  { id: 'clotureur', name: 'Clôturier', module: 'Comptabilité', icon: '🏛', pitch: 'Pilote la clôture annuelle 12 étapes — collecte, amort., IS, états financiers.', model: 'Opus 4.7', phase: 2 },
  { id: 'liassier', name: 'Liassier', module: 'Audit', icon: '📑', pitch: 'Pré-remplissage liasse fiscale — annexes 1 (bilan) + 2 (compte de résultat) avec contrôles.', model: 'Sonnet 4.6', phase: 2 },
  // Phase 3 — Opus
  { id: 'controleur_dgi', name: 'Contrôleur DGI ★', module: 'Fiscal', icon: '🛡', pitch: 'Audit fiscal complet 7 axes — journal, IS, déductibilité, CCA, recettes, TVA, immo, RAS.', model: 'Opus 4.7', phase: 3 },
  { id: 'veilleur_jort', name: 'Veilleur JORT', module: 'Fiscal', icon: '📰', pitch: 'Veille réglementaire JORT — décrets et arrêtés tunisiens scorés par pertinence PME.', model: 'Opus 4.7', phase: 3 },
  { id: 'optimisateur', name: 'Optimisateur', module: 'Fiscal', icon: '🎯', pitch: "Analyse what-if comparant scénarios d'optimisation IS — secteur, dotation, crédit d'impôt.", model: 'Opus 4.7', phase: 3 },
  // Phase 4
  { id: 'cac_oect', name: 'CAC OECT', module: 'Audit', icon: '✅', pitch: "Pré-rapport Commissaire aux Comptes — opinion d'audit, ratios financiers, zones d'audit.", model: 'Sonnet 4.6', phase: 4 },
  { id: 'drh_cnss', name: 'DRH/CNSS', module: 'Extension', icon: '👥', pitch: 'Paie + déclarations sociales — CNSS, TFP, Foprolos, RAS consultants.', model: 'Sonnet 4.6', phase: 4 },
  { id: 'juriste_rne', name: 'Juriste RNE', module: 'Extension', icon: '⚖️', pitch: 'Actes juridiques — PV AGO, statuts SARL, décision gérant, citations COC + LCS.', model: 'Sonnet 4.6', phase: 4 },
  { id: 'tresorier', name: 'Trésorier', module: 'Extension', icon: '💰', pitch: 'Projection cashflow rolling 13 sem. ou 12 mois + alertes solde + DSO.', model: 'Sonnet 4.6', phase: 4 },
];

export function DashboardPage() {
  const [health, setHealth] = useState<{ environment: string; timestamp: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .health()
      .then((h) => setHealth({ environment: h.environment, timestamp: h.timestamp }))
      .catch((e: Error) => setError(e.message));
  }, []);

  const phases: Array<{ phase: 1 | 2 | 3 | 4; label: string }> = [
    { phase: 1, label: 'Phase 1 · Cœur métier' },
    { phase: 2, label: 'Phase 2 · Comptabilité avancée' },
    { phase: 3, label: 'Phase 3 · Fiscal stratégique (Opus)' },
    { phase: 4, label: 'Phase 4 · Extensions cabinet' },
  ];

  return (
    <div className="space-y-12">
      <section>
        <div className="mb-2">
          <span className="text-xs uppercase tracking-wider text-taupe">Mizan Platform · 16 agents · Phase 1-4</span>
        </div>
        <h2 className="font-serif text-3xl font-bold mb-2">Tableau de bord</h2>
        <p className="text-taupe max-w-3xl">
          Couche d'intelligence d'eFactura Suite — chaque agent compose des moteurs déterministes
          (engine-tva, engine-sce-classifier, engine-journal-sce, engine-is, engine-ras,
          engine-amortissement, engine-rne-fetcher, engine-ttn-submitter, engine-rapprochement-matcher,
          engine-template-relance, engine-template-juridique, engine-cashflow-projection,
          engine-controle-dgi, engine-liasse, engine-jort-scraper, engine-pdf-bank-extractor,
          engine-ratio…) et retourne un résultat traçable jusqu'à la ligne.
        </p>
        <div className="mt-4 flex items-center gap-3 text-xs">
          {health && (
            <>
              <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-800 border border-emerald-300 font-medium">
                API · {health.environment}
              </span>
              <span className="text-taupe">checked-in {new Date(health.timestamp).toLocaleString('fr-FR')}</span>
            </>
          )}
          {error && (
            <span className="px-2 py-1 rounded bg-red-100 text-red-800 border border-red-300">
              API injoignable : {error}
            </span>
          )}
        </div>
      </section>

      {phases.map(({ phase, label }) => {
        const agents = AGENTS.filter((a) => a.phase === phase);
        if (agents.length === 0) return null;
        return (
          <section key={phase}>
            <h3 className="font-serif text-xl mb-4">{label}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {agents.map((a) => (
                <Link
                  key={a.id}
                  to={`/agents/${a.id}`}
                  className="card hover:border-gold hover:shadow-md transition group"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-taupe">
                        {a.module}
                      </div>
                      <div className="font-serif text-xl font-bold mt-1 group-hover:text-gold transition">
                        {a.name}
                      </div>
                    </div>
                    <div className="text-3xl">{a.icon}</div>
                  </div>
                  <p className="text-sm text-taupe mb-3">{a.pitch}</p>
                  <div className="text-xs font-mono text-taupe">{a.model}</div>
                </Link>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
